/**
 * Vignobles › Index des appellations (/vignobles/appellations) — table cherchable des
 * appellations viticoles (tous pays), regroupées par nom : pays, vignoble, couleur/cépages.
 * Ajout assisté par IA (volet droite), navigation par lettre (barre alphabet + saisie).
 * La barre d'outils (recherche + alphabet) reste fixe ; seule la liste défile.
 */
import { useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Plus } from 'lucide-react';
import { vignoblesApi } from '../../services/api';
import { Spinner, EmptyState, PageTitle, Button } from '../../components/ui/kit';
import { AppellationDrawer } from '../../components/wine/AppellationDrawer';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const flat = (s) => norm(s).replace(/[-\s]+/g, ' ');
const firstLetter = (s) => {
  const c = norm(s).charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : '#';
};

export default function Appellations({ canEditIndex = false }) {
  const [q, setQ] = useState('');
  const [drawerName, setDrawerName] = useState(null); // null=fermé ; ''=ajout ; nom=édition
  const [paysFilter, setPaysFilter] = useState('');
  const listRef = useRef(null);
  const { data: allRows = [], isLoading } = useQuery({
    queryKey: ['appellations', ''],
    queryFn: () => vignoblesApi.appellations(''),
  });

  const paysList = useMemo(() => [...new Set(allRows.map((a) => a.pays || 'France'))].sort(), [allRows]);

  const rows = useMemo(() => {
    let base = paysFilter ? allRows.filter((a) => (a.pays || 'France') === paysFilter) : allRows;
    const term = flat(q.trim());
    if (!term) return base;
    // Saisie d'une seule lettre → appellations qui COMMENCENT par cette lettre.
    if (term.length === 1) return base.filter((a) => flat(a.appellation).startsWith(term));
    return base.filter((a) => flat(a.appellation).includes(term) || flat(a.vignoble).includes(term));
  }, [allRows, q, paysFilter]);

  const anchors = useMemo(() => {
    const seen = {};
    rows.forEach((a) => { const L = firstLetter(a.appellation); if (!(L in seen)) seen[L] = a.appellation; });
    return seen;
  }, [rows]);
  const availableLetters = useMemo(() => new Set(Object.keys(anchors)), [anchors]);

  // Défile UNIQUEMENT à l'intérieur du conteneur de liste (header/menu/alphabet restent fixes).
  const jumpTo = (letter) => {
    const container = listRef.current;
    const el = container?.querySelector(`[data-letter-anchor="${letter}"]`);
    if (container && el) container.scrollTop = el.offsetTop - container.offsetTop;
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl h-full flex flex-col min-h-0">
      <div className="flex items-start justify-between gap-3 shrink-0">
        <PageTitle kicker="Vignobles" title="Index des appellations" />
        {canEditIndex && (
          <Button variant="primary" onClick={() => setDrawerName(q.trim().length > 1 ? q.trim() : '')} data-testid="add-appellation-btn">
            <Plus className="w-4 h-4" /> Ajouter une appellation
          </Button>
        )}
      </div>
      <p className="text-[13px] text-n600 mt-1 shrink-0">Liste presque complète des appellations du vignoble français.</p>

      {/* Pays présents dans l'index (bulles filtrantes) */}
      <div className="flex flex-wrap gap-1.5 mt-3 shrink-0" data-testid="pays-bubbles">
        <button onClick={() => setPaysFilter('')} data-testid="pays-all"
          className={`px-3 h-7 rounded-full text-[12px] border transition-colors ${!paysFilter ? 'bg-gold800 text-white border-gold800' : 'border-hair text-n700 hover:bg-gold100'}`}>
          Tous ({allRows.length})
        </button>
        {paysList.map((p) => {
          const count = allRows.filter((a) => (a.pays || 'France') === p).length;
          return (
            <button key={p} onClick={() => setPaysFilter(p)} data-testid={`pays-${p}`}
              className={`px-3 h-7 rounded-full text-[12px] border transition-colors ${paysFilter === p ? 'bg-gold800 text-white border-gold800' : 'border-hair text-n700 hover:bg-gold100'}`}>
              {p} ({count})
            </button>
          );
        })}
      </div>

      <div className="relative max-w-md mt-4 mb-3 shrink-0">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-n500" />
        <input className="vd-input h-9 pl-8" placeholder="Rechercher (ou tapez une lettre)…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="appellation-search" />
      </div>

      <div className="flex flex-wrap gap-1 mb-3 shrink-0" data-testid="alphabet-bar">
        {ALPHABET.map((L) => {
          const enabled = availableLetters.has(L);
          return (
            <button key={L} disabled={!enabled} onClick={() => jumpTo(L)} data-testid={`alpha-${L}`}
              className={`w-7 h-7 rounded text-[12px] font-medium transition-colors ${enabled ? 'text-gold800 hover:bg-gold100' : 'text-n500/40 cursor-default'}`}>
              {L}
            </button>
          );
        })}
      </div>

      {isLoading ? <Spinner /> : rows.length === 0 ? (
        <EmptyState title="Aucune appellation" description="Aucune appellation ne correspond à votre recherche." testId="appellations-empty" />
      ) : (
        <>
          <p className="text-[12px] text-n500 mb-2 nums shrink-0">{rows.length} appellation(s)</p>
          <div className="vd-card overflow-auto flex-1 min-h-0" ref={listRef}>
            <table className="w-full table-fixed text-[13.5px]" data-testid="appellations-table">
              <colgroup>
                <col style={{ width: '25%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '46%' }} />
              </colgroup>
              <thead className="sticky top-0 bg-paper z-10"><tr className="text-left kicker">
                <th className="py-2.5 px-3">Appellation</th>
                <th className="py-2.5 px-3">Pays</th>
                <th className="py-2.5 px-3">Vignoble</th>
                <th className="py-2.5 px-3">Couleur / cépages classiques</th>
              </tr></thead>
              <tbody>
                {rows.map((a) => {
                  const L = firstLetter(a.appellation);
                  const isAnchor = anchors[L] === a.appellation;
                  return (
                    <tr key={a.appellation} className="hair-t align-top cursor-pointer hover:bg-surface/60" data-testid={`appellation-row-${a.appellation}`}
                      onClick={() => setDrawerName(a.appellation)}
                      {...(isAnchor ? { 'data-letter-anchor': L } : {})}>
                      <td className="py-2.5 px-3 font-title text-lg">{a.appellation}</td>
                      <td className="py-2.5 px-3 text-n600 whitespace-nowrap">{a.pays || 'France'}</td>
                      <td className="py-2.5 px-3 text-n600">{a.vignoble}{a.sous_region ? ` · ${a.sous_region}` : ''}</td>
                      <td className="py-2.5 px-3 text-n700">
                        {a.entries.map((e, i) => (
                          <div key={i} className="mb-1 last:mb-0">
                            {e.couleur && <span className="text-n500">{e.couleur} — </span>}{e.cepages || '—'}
                          </div>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AppellationDrawer open={drawerName !== null} initialName={drawerName || ''} readOnly={!canEditIndex} onClose={() => setDrawerName(null)} />
    </div>
  );
}
